import { captureException } from "@sentry/browser"
import { actionAlert, toast } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { Profile } from "api/resources/profile/types"
import { PhoneNumber } from "api/resources/users/types"
import { AuthManager } from "features/Authentication/AuthManager"
import { useGoogleReCaptcha } from "react-google-recaptcha-hook"
import { buildPhoneNumber, buildUserName, mapErrorsV2 } from "utils/helpers"
import { CaptchaType } from "utils/hooks/useGRecaptcha"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

interface UseRegisterMasterclassOptions {
  slug: string
  profile: Profile | null
  onSuccess?: () => void
  onError?: (e: any) => void
}

const getRelevantPhoneNumber = (
  profile: Profile | null,
  phoneNumber?: PhoneNumber
) => {
  if (phoneNumber) {
    return buildPhoneNumber({
      code: phoneNumber.code,
      number: phoneNumber.number,
    })
  }
  if (profile?.phoneNumber) {
    return buildPhoneNumber({
      code: profile.phoneNumber.code,
      number: profile.phoneNumber.number,
    })
  }

  return null
}

export const useRegisterMasterclass = ({
  slug,
  profile,
  onSuccess,
  onError,
}: UseRegisterMasterclassOptions) => {
  const { executeGoogleReCaptcha } = useGoogleReCaptcha(
    import.meta.env.VITE_INVISIBLE_RECAPTCHA_KEY,
    {
      enterprise: true,
    }
  )

  const buildPostData = (updatedPhoneNumber: string | null, token: string) => {
    return {
      preRegistration: false,
      whatsappUpdates: false,
      email: AuthManager.getPlatformAuth()?.user?.email || "",
      fullName: buildUserName({
        firstName: profile?.user?.firstName || "",
        lastName: profile?.user?.lastName || "",
      }),
      phoneNumber: updatedPhoneNumber,
      captcha: {
        value: token,
        type: CaptchaType.invisible,
      },
    }
  }

  const mutation = useMutation({
    mutationKey: ["register-masterclass", slug],
    mutationFn: async ({ phoneNumber }: { phoneNumber?: PhoneNumber }) => {
      const token = await executeGoogleReCaptcha("masterclass")

      if (!token) {
        toast.error("Captcha failed. Try again.")
        return
      }

      trackingService.trackEvent(
        TrackingEvents.masterclassRegistrationAttempted,
        {
          masterclass_slug: slug,
          email: AuthManager.getPlatformAuth()?.user?.email || "",
        },
        { services: ["PostHog"] }
      )

      const data = buildPostData(
        getRelevantPhoneNumber(profile, phoneNumber),
        token
      )

      return api.masterclasses.registration.create({
        data,
        urlParams: { masterclassSlug: slug },
      })
    },
    onSuccess: () => {
      trackingService.trackEvent(
        TrackingEvents.masterclassRegistrationCompleted,
        {
          masterclass_slug: slug,
          email: AuthManager.getPlatformAuth()?.user?.email || "",
        },
        { services: ["PostHog"] }
      )
      actionAlert.success({
        title: "Reservation Successful",
        duration: 3000,
      })
      onSuccess?.()
    },
    onError: (e, v) => {
      captureException(new Error("Masterclass Registration Error"), scope => {
        scope.setExtras({
          error: JSON.stringify(e),
          data: buildPostData(
            getRelevantPhoneNumber(profile, v?.phoneNumber),
            ""
          ),
          slug,
        })
        return scope
      })

      mapErrorsV2(e)
      onError?.(e)
    },
  })

  return mutation
}
